import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import {
  Bell, BellRing, Trash2, Plus, Loader2, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { usePortfolioStore } from '../../../state/portfolioStore';
import SectionNav from '../../../components/layout/SectionNav';
import PageHeader from '../../../components/layout/PageHeader';
import { portfolioTabs } from '../../../nav/publicNav';

export default function AlertsPage() {
  const alerts = usePortfolioStore((s) => s.alerts);
  const alertsLoading = usePortfolioStore((s) => s.alertsLoading);
  const fetchAlerts = usePortfolioStore((s) => s.fetchAlerts);
  const createAlert = usePortfolioStore((s) => s.createAlert);
  const deleteAlert = usePortfolioStore((s) => s.deleteAlert);
  const checkAlerts = usePortfolioStore((s) => s.checkAlerts);
  const holdings = usePortfolioStore((s) => s.holdings);
  const fetchHoldings = usePortfolioStore((s) => s.fetchHoldings);

  const [showForm, setShowForm] = useState(false);
  const [symbol, setSymbol] = useState('');
  const [alertType, setAlertType] = useState('price_above');
  const [threshold, setThreshold] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!holdings.length) fetchHoldings();
    fetchAlerts();
  }, [fetchHoldings, fetchAlerts, holdings.length]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!symbol || !threshold) return;
    setCreating(true);
    const h = holdings.find((h) => h.symbol === symbol);
    await createAlert({
      symbol,
      company_name: h?.company_name || h?.companyName || symbol,
      alert_type: alertType,
      threshold: parseFloat(threshold),
    });
    setShowForm(false);
    setSymbol('');
    setThreshold('');
    setCreating(false);
  };

  const handleCheck = async () => {
    const triggered = await checkAlerts();
    if (triggered.length > 0) {
      alert(`${triggered.length} alert(s) triggered!`);
    }
  };

  const activeAlerts = alerts.filter((a) => a.active && !a.triggered);
  const triggeredAlerts = alerts.filter((a) => a.triggered);

  const typeLabel = (t) => {
    switch (t) {
      case 'price_above': return 'Price Above';
      case 'price_below': return 'Price Below';
      case 'pct_change': return '% Change';
      default: return t;
    }
  };

  return (
    <div className="space-y-4">
      <SectionNav tabs={portfolioTabs} />
      <PageHeader
        title="Price Alerts"
        description="Get notified when your holdings hit target prices"
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={handleCheck}>
              <BellRing className="w-3 h-3" /> Check Now
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs" onClick={() => setShowForm(!showForm)}>
              <Plus className="w-3.5 h-3.5" /> New Alert
            </Button>
          </div>
        }
      />

      {/* Create Form */}
      {showForm && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="p-4">
            <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3">
              <div className="space-y-1 flex-1 min-w-[150px]">
                <Label className="text-xs">Stock</Label>
                <Select value={symbol} onValueChange={setSymbol}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select holding" /></SelectTrigger>
                  <SelectContent>
                    {holdings.map((h) => (
                      <SelectItem key={h.symbol || h.id} value={h.symbol}>{h.symbol} - {h.company_name || h.companyName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 w-[160px]">
                <Label className="text-xs">Type</Label>
                <Select value={alertType} onValueChange={setAlertType}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="price_above">Price Above</SelectItem>
                    <SelectItem value="price_below">Price Below</SelectItem>
                    <SelectItem value="pct_change">% Change (day)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 w-[120px]">
                <Label className="text-xs">{alertType === 'pct_change' ? 'Threshold %' : 'Price ₹'}</Label>
                <Input type="number" className="h-8 text-xs" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="0" step="0.01" />
              </div>
              <Button type="submit" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8" disabled={creating || !symbol || !threshold}>
                {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create'}
              </Button>
              <Button type="button" size="sm" variant="ghost" className="text-xs h-8" onClick={() => setShowForm(false)}>Cancel</Button>
            </form>
          </CardContent>
        </Card>
      )}

      {alertsLoading && alerts.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Active Alerts */}
          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                <Bell className="w-4 h-4 text-emerald-500" /> Active Alerts ({activeAlerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {activeAlerts.length > 0 ? (
                <div className="divide-y divide-border/30">
                  {activeAlerts.map((a) => (
                    <div key={a.id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{a.symbol}</p>
                          <Badge variant="secondary" className="text-[10px]">{typeLabel(a.alert_type)}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {a.alert_type === 'pct_change'
                            ? `Triggers when day change exceeds ${a.threshold}%`
                            : `Triggers when price ${a.alert_type === 'price_above' ? 'rises above' : 'drops below'} ₹${a.threshold}`
                          }
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => deleteAlert(a.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Bell className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No active alerts. Create one to get started.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Triggered */}
          {triggeredAlerts.length > 0 && (
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-amber-500" /> Triggered ({triggeredAlerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/30">
                  {triggeredAlerts.map((a) => (
                    <div key={a.id} className="flex items-center justify-between px-4 py-3 opacity-70">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{a.symbol}</p>
                          <Badge variant="secondary" className="text-[10px]">{typeLabel(a.alert_type)}</Badge>
                          <Badge className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Triggered</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Triggered at ₹{a.trigger_price?.toFixed(2)} {a.triggered_at ? `on ${new Date(a.triggered_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => deleteAlert(a.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
